import { NetworkError, deepCamelCase, searchQueryString } from '../../../fetchers/_helpers';

/**
 * This file was automatically generated by @gctl/openapi-fetchers
 * Do not modify this file, any changes will be destroyed when
 * the command is rerun.
 */

export type GetVideoQuestionsActivationFields = readonly string[];

/** Options for GET /videos/{video_id}/questions/activation */
export type GetVideoQuestionsActivationOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/questions/activation */
  query?: {
    /**
     * The privacy-enabled password required to access the video. The password can be sent as plaintext, such as `hunter2`, or as a SHA-1 hash, such as `f3bbbd66a63d4bf1747940578ec3d0103530e21d`.
     */
    password?: string;

    /**
     * The source of the request.
     *
     * Option descriptions:
     *  * `backend` - The request originates from backend systems.
     *  * `site` - The request originates from the Vimeo website.
     *  * `test` - The request originates from tests.
     *  * `upload` - The request originates from the upload flow.
     *
     */
    source?: `backend` | `site` | `test` | `upload`;
  };
};

/** Result type for GET /videos/{video_id}/questions/activation */
export type GetVideoQuestionsActivationResult<Paths extends readonly string[]> = unknown;

/**
 * Get the status of whether the user can ask video questions
 * This method returns the status of whether the authenticated user can ask questions about the specified video.
 */
export async function getVideoQuestionsActivation<Paths extends GetVideoQuestionsActivationFields>({
  baseUrl,
  where: { videoId },
  query,
  ...options
}: GetVideoQuestionsActivationOptions<Paths>): Promise<GetVideoQuestionsActivationResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/questions/activation?${searchQueryString(query)}`,
    {
      ...options,
      method: 'GET',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as GetVideoQuestionsActivationResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}

export type PostVideoQuestionsActivationFields = readonly string[];

/** Options for POST /videos/{video_id}/questions/activation */
export type PostVideoQuestionsActivationOptions<Paths extends readonly string[]> = Omit<
  RequestInit,
  'method' | 'body'
> & {
  baseUrl?: string;

  where: {
    /**
     * The ID of the video.
     */
    videoId: number;
  };

  /** Possible query parameters for /videos/{video_id}/questions/activation */
  query?: {
    /**
     * Whether to bypass cached AI data and force regeneration for testing purposes.
     */
    overrideCache?: boolean;

    /**
     * The privacy-enabled password required to access the video. The password can be sent as plaintext, such as `hunter2`, or as a SHA-1 hash, such as `f3bbbd66a63d4bf1747940578ec3d0103530e21d`.
     */
    password?: string;

    /**
     * The electronic signature of the guest.
     */
    signature?: string;

    /**
     * The source of the request.
     *
     * Option descriptions:
     *  * `backend` - The request originates from backend systems.
     *  * `site` - The request originates from the Vimeo website.
     *  * `test` - The request originates from tests.
     *  * `upload` - The request originates from the upload flow.
     *
     */
    source?: `backend` | `site` | `test` | `upload`;
  };
};

/** Result type for POST /videos/{video_id}/questions/activation */
export type PostVideoQuestionsActivationResult<Paths extends readonly string[]> = unknown;

/**
 * Enable the user to ask questions about a video
 * This method enables the authenticated user to ask questions about the specified video.
 *
 * This method works only for videos with completed transcripts in English.
 * The authenticated user must have permission to edit the video.
 */
export async function postVideoQuestionsActivation<
  Paths extends PostVideoQuestionsActivationFields,
>({
  baseUrl,
  where: { videoId },
  query,
  ...options
}: PostVideoQuestionsActivationOptions<Paths>): Promise<PostVideoQuestionsActivationResult<Paths>> {
  let res = await fetch(
    `${baseUrl}/videos/${videoId}/questions/activation?${searchQueryString(query)}`,
    {
      ...options,
      method: 'POST',
    },
  );

  if (!res.ok) {
    throw new NetworkError('A network error occurred', res.status, res);
  }

  if (res.status === 204) {
    return null as unknown as PostVideoQuestionsActivationResult<Paths>;
  }

  if (!res.headers.get('content-type')?.match(/^application\/(.+)?json$/)) {
    throw new Error('Expected JSON response');
  }

  let json = await res.json();
  return deepCamelCase(json);
}
